import type { Stripe } from '@stripe/stripe-js';
import classNames from 'classnames';
import { type ReactNode, memo, useEffect, useState } from 'react';

import { MappedPaymentMethodComponent } from '@features/Stripe/widgets/checkout-selector/widgets/payment-option-tab';

import { UiKitButton } from '@components/common/ui-kit-button';
import { selectedPaymentMethodTabAtom } from '@features/Stripe/atoms';
import { usePaymentRequest } from '@features/Stripe/hooks';
import { useSetAtom } from 'jotai';
import styles from './compact-selector.module.scss';

type TpCompactSelectorProps = {
  paymentMethods: {
    paymentMethodName: 'PayPal' | 'Google Pay' | 'Apple Pay' | 'Credit card';
    paymentMethodLogo: ReactNode;
    paymentMethodLogoCompact: ReactNode;
    paymentMethodContentList: string[] | undefined;
    paymentButtonText: string | undefined;
  }[];
  showOnlyPaymentRequestButton?: boolean;
  handleScrollToSecondSection?: () => void;
  stripe: Stripe | null;
  clientSecret: string;
  userEmail: string;
  cancelSubscriptionAtPeriodEnd?: boolean;
  sendAddPaymentInfoEvents: ({
    paymentMethod,
  }: {
    paymentMethod: string;
  }) => void;
  handleProcessingStatus: (status: boolean) => void;
  onSuccess: ({
    paymentMethod,
    paymentSystem,
  }: {
    paymentMethod: string;
    paymentSystem?: 'stripe' | 'braintree';
  }) => Promise<void>;
  planInfo: {
    priceId: string;
    planName: string;
    fullPriceDiscount: number;
    billingPeriod: number;
    billingPeriodUnit: string;
    introOffer?: string;
    currency: string;
    braintreePlanId?: string;
    braintreeMerchantId?: string;
  };
};

export const CompactSelector = memo((props: TpCompactSelectorProps) => {
  const {
    paymentMethods,
    stripe,
    clientSecret,
    userEmail,
    sendAddPaymentInfoEvents,
    handleProcessingStatus,
    onSuccess,
    cancelSubscriptionAtPeriodEnd,
    planInfo,
    showOnlyPaymentRequestButton,
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState(
    paymentMethods[paymentMethods.length - 1],
  );

  const setPaymentMethodAtom = useSetAtom(selectedPaymentMethodTabAtom);
  useEffect(
    () =>
      setPaymentMethodAtom({
        paymentMethod: selectedMethod.paymentMethodName,
      }),
    [selectedMethod, setPaymentMethodAtom],
  );

  const { paymentMethodResult } = usePaymentRequest({
    stripe,
    planInfo,
  });

  if (showOnlyPaymentRequestButton) {
    if (
      paymentMethodResult === 'applePay' ||
      paymentMethodResult === 'googlePay'
    ) {
      const isButtonLoading = !document.querySelector('#paymentReqBtnId');

      return (
        <div className={styles.paymentOptionSubmitButton}>
          <div
            className={classNames(styles.buttonWrapper, styles.ex)}
            style={{
              opacity: isButtonLoading ? 1 : 0.0001,
            }}
          >
            <MappedPaymentMethodComponent
              paymentReqBtnId="paymentReqBtnId"
              key={`${planInfo.planName}-${planInfo.fullPriceDiscount}-${planInfo.currency}`}
              paymentMethodName={
                paymentMethods.filter((item) =>
                  item.paymentMethodName
                    .toLowerCase()
                    .includes(paymentMethodResult.slice(0, 5)),
                )[0]?.paymentMethodName
              }
              stripe={stripe}
              clientSecret={clientSecret}
              userEmail={userEmail}
              cancelSubscriptionAtPeriodEnd={cancelSubscriptionAtPeriodEnd}
              onLoadEnd={() => setIsLoading(false)}
              sendAddPaymentInfoEvents={sendAddPaymentInfoEvents}
              handleProcessingStatus={handleProcessingStatus}
              onSuccess={onSuccess}
              planInfo={planInfo}
            />
          </div>

          <div className={styles.buttonCoverage}>
            Continue with
            {
              paymentMethods.filter((item) =>
                item.paymentMethodName
                  .toLowerCase()
                  .includes(paymentMethodResult.slice(0, 5)),
              )[0]?.paymentMethodLogoCompact
            }
          </div>
        </div>
      );
    } else {
      return (
        <UiKitButton
          as="a"
          href="#swpCompactCheckout"
          fullWidth
          style={{ marginTop: 16 }}
        >
          GET MY PLAN
        </UiKitButton>
      );
    }
  }

  return (
    <>
      <div className={styles.selectorTabs}>
        {paymentMethods.map((paymentMethod) => (
          <div
            key={paymentMethod.paymentMethodName}
            className={classNames(
              styles.paymentOptionTab,
              selectedMethod.paymentMethodName ===
                paymentMethod.paymentMethodName &&
                styles.paymentOptionTabSelected,
            )}
            onClick={() => {
              setSelectedMethod(paymentMethod);
            }}
          >
            {paymentMethod.paymentMethodLogoCompact}
          </div>
        ))}
      </div>

      <div className={styles.paymentOptionSubmitButton}>
        <div
          className={styles.buttonCoverage}
          style={{
            opacity:
              selectedMethod.paymentMethodName === 'Credit card' || isLoading
                ? 0
                : 1,
            backgroundColor:
              selectedMethod.paymentMethodName === 'PayPal'
                ? '#F6C657'
                : '#000',
            color:
              selectedMethod.paymentMethodName === 'PayPal' ? '#000' : '#fff',
          }}
        >
          Continue with {selectedMethod.paymentMethodLogoCompact}
        </div>
        <div
          className={styles.buttonWrapper}
          style={{
            opacity:
              selectedMethod.paymentMethodName === 'Credit card' || isLoading
                ? 1
                : 0.0001,
          }}
        >
          <MappedPaymentMethodComponent
            paymentMethodName={selectedMethod.paymentMethodName}
            stripe={stripe}
            clientSecret={clientSecret}
            userEmail={userEmail}
            cancelSubscriptionAtPeriodEnd={cancelSubscriptionAtPeriodEnd}
            onLoadEnd={() => setIsLoading(false)}
            sendAddPaymentInfoEvents={sendAddPaymentInfoEvents}
            handleProcessingStatus={handleProcessingStatus}
            onSuccess={onSuccess}
            planInfo={planInfo}
          />
        </div>
      </div>
    </>
  );
});

CompactSelector.displayName = 'CompactSelector';
